import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, ArrowRight, Map as MapIcon, Stethoscope } from '../components/Icons.jsx';

// Medic Mind Career Camp host cities — featured pins with their own icon.
const CAREER_CAMP = [
  { id: 'cc-london', name: 'Career Camp · London', kind: 'Career Camp', city: 'London', lat: 51.5181, lng: -0.0593, info: '5-day cohort. Hospital + hospice rotations. Bursaries 50–100%.' },
  { id: 'cc-birmingham', name: 'Career Camp · Birmingham', kind: 'Career Camp', city: 'Birmingham', lat: 52.4527, lng: -1.9438, info: 'Clinician-led workshops + hospital visits. Mid-country Year 12 favourite.' },
  { id: 'cc-manchester', name: 'Career Camp · Manchester', kind: 'Career Camp', city: 'Manchester', lat: 53.4625, lng: -2.2253, info: 'Live GP-surgery placement + hospital rotations.' }
];

// Major UK NHS hospitals + teaching trusts. Approx city-centre lat/lng of the main site.
const PLACES = [
  { id: 1, name: 'Royal London Hospital', kind: 'Hospital', city: 'London', lat: 51.5181, lng: -0.0593 },
  { id: 2, name: "St Thomas' Hospital", kind: 'Hospital', city: 'London', lat: 51.4983, lng: -0.1190 },
  { id: 3, name: "King's College Hospital", kind: 'Hospital', city: 'London', lat: 51.4685, lng: -0.0939 },
  { id: 4, name: 'University College Hospital', kind: 'Hospital', city: 'London', lat: 51.5246, lng: -0.1352 },
  { id: 5, name: 'Imperial · Charing Cross Hospital', kind: 'Hospital', city: 'London', lat: 51.4861, lng: -0.2207 },
  { id: 6, name: 'St Christopher\'s Hospice', kind: 'Hospice', city: 'London', lat: 51.4194, lng: -0.0552 },
  { id: 7, name: 'Manchester Royal Infirmary', kind: 'Hospital', city: 'Manchester', lat: 53.4625, lng: -2.2253 },
  { id: 8, name: 'Salford Royal Hospital', kind: 'Hospital', city: 'Salford', lat: 53.4878, lng: -2.3145 },
  { id: 9, name: 'St Ann\'s Hospice', kind: 'Hospice', city: 'Manchester', lat: 53.4181, lng: -2.2630 },
  { id: 10, name: 'Queen Elizabeth Hospital Birmingham', kind: 'Hospital', city: 'Birmingham', lat: 52.4527, lng: -1.9438 },
  { id: 11, name: 'Birmingham Heartlands Hospital', kind: 'Hospital', city: 'Birmingham', lat: 52.4886, lng: -1.8330 },
  { id: 12, name: 'Bristol Royal Infirmary', kind: 'Hospital', city: 'Bristol', lat: 51.4584, lng: -2.5984 },
  { id: 13, name: 'Leeds General Infirmary', kind: 'Hospital', city: 'Leeds', lat: 53.8011, lng: -1.5523 },
  { id: 14, name: 'St James\'s University Hospital', kind: 'Hospital', city: 'Leeds', lat: 53.8082, lng: -1.5167 },
  { id: 15, name: 'Sheffield Teaching Hospitals', kind: 'Hospital', city: 'Sheffield', lat: 53.3793, lng: -1.4828 },
  { id: 16, name: 'Royal Liverpool University Hospital', kind: 'Hospital', city: 'Liverpool', lat: 53.4060, lng: -2.9657 },
  { id: 17, name: 'Royal Victoria Infirmary', kind: 'Hospital', city: 'Newcastle', lat: 54.9805, lng: -1.6193 },
  { id: 18, name: 'Royal Hallamshire Hospital', kind: 'Hospital', city: 'Sheffield', lat: 53.3776, lng: -1.4912 },
  { id: 19, name: 'Edinburgh Royal Infirmary', kind: 'Hospital', city: 'Edinburgh', lat: 55.9223, lng: -3.1336 },
  { id: 20, name: 'Glasgow Royal Infirmary', kind: 'Hospital', city: 'Glasgow', lat: 55.8639, lng: -4.2378 },
  { id: 21, name: 'Queen Elizabeth University Hospital', kind: 'Hospital', city: 'Glasgow', lat: 55.8579, lng: -4.3382 },
  { id: 22, name: 'Aberdeen Royal Infirmary', kind: 'Hospital', city: 'Aberdeen', lat: 57.1373, lng: -2.1330 },
  { id: 23, name: 'Ninewells Hospital', kind: 'Hospital', city: 'Dundee', lat: 56.4639, lng: -3.0467 },
  { id: 24, name: 'University Hospital of Wales', kind: 'Hospital', city: 'Cardiff', lat: 51.5071, lng: -3.1864 },
  { id: 25, name: 'University Hospital Southampton', kind: 'Hospital', city: 'Southampton', lat: 50.9337, lng: -1.4357 },
  { id: 26, name: 'Royal Devon University Hospital', kind: 'Hospital', city: 'Exeter', lat: 50.7305, lng: -3.4940 },
  { id: 27, name: 'Derriford Hospital', kind: 'Hospital', city: 'Plymouth', lat: 50.4159, lng: -4.1117 },
  { id: 28, name: 'Norfolk and Norwich University Hospital', kind: 'Hospital', city: 'Norwich', lat: 52.6184, lng: 1.2222 },
  { id: 29, name: 'University Hospital Coventry', kind: 'Hospital', city: 'Coventry', lat: 52.4194, lng: -1.5618 },
  { id: 30, name: 'Royal Hospital for Children & Young People', kind: 'Hospital', city: 'Edinburgh', lat: 55.9229, lng: -3.1340 },
  { id: 31, name: 'St Anne\'s Hospice Care', kind: 'Hospice', city: 'Birmingham', lat: 52.4823, lng: -1.8904 },
  { id: 32, name: 'Sue Ryder · Wheatfields Hospice', kind: 'Hospice', city: 'Leeds', lat: 53.8392, lng: -1.5784 },
  { id: 33, name: 'Marie Curie Hospice Bradford', kind: 'Hospice', city: 'Bradford', lat: 53.7960, lng: -1.7594 },
  { id: 34, name: 'St Catherine\'s Hospice', kind: 'Hospice', city: 'Crawley', lat: 51.1145, lng: -0.1872 },
  { id: 35, name: 'Brighton & Hove Hospice', kind: 'Hospice', city: 'Brighton', lat: 50.8443, lng: -0.1305 }
];

const UK_CENTRE = [54.0, -2.5];

const kindIcon = {
  Hospital: new L.DivIcon({ className: 'mm-pin', iconSize: [26, 26], html: '<div style="background:#653192;color:#fff;width:26px;height:26px;border-radius:50%;display:grid;place-items:center;font-size:14px;font-weight:700;border:2px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.25)">H</div>' }),
  Hospice: new L.DivIcon({ className: 'mm-pin', iconSize: [26, 26], html: '<div style="background:#3ab8a6;color:#fff;width:26px;height:26px;border-radius:50%;display:grid;place-items:center;font-size:14px;font-weight:700;border:2px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.25)">P</div>' }),
  'Career Camp': new L.DivIcon({ className: 'mm-pin mm-pin-cc', iconSize: [40, 40], html: '<div style="position:relative;width:40px;height:40px"><div style="position:absolute;inset:-4px;border-radius:50%;background:rgba(124,62,175,0.25);animation:mm-pulse 2s ease-in-out infinite"></div><div style="position:relative;background:linear-gradient(135deg,#653192,#3ab8a6);color:#fff;width:40px;height:40px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-weight:800;border:3px solid #fff;box-shadow:0 6px 16px rgba(0,0,0,0.3);font-family:Montserrat,sans-serif;letter-spacing:0.04em">CAMP</div></div>' })
};
const userIcon = new L.DivIcon({ className: 'mm-pin', iconSize: [22, 22], html: '<div style="background:#23143f;width:22px;height:22px;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,0.4)"></div>' });

function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat); const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

function FlyTo({ coords, zoom = 11 }) {
  const map = useMap();
  useEffect(() => {
    if (coords) map.flyTo([coords.lat, coords.lng], zoom, { duration: 0.8 });
  }, [coords, zoom, map]);
  return null;
}

export default function WeMap() {
  const [postcode, setPostcode] = useState('');
  const [home, setHome] = useState(null);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [kindFilter, setKindFilter] = useState('all');

  const lookup = async () => {
    const raw = postcode.trim();
    if (!raw) { setStatus({ state: 'error', message: 'Enter a postcode (or browse the UK-wide view below).' }); return; }
    setStatus({ state: 'loading', message: '' });
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(raw)}`);
      if (res.status === 404) { setStatus({ state: 'error', message: 'Postcode not found.' }); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const lat = data?.result?.latitude;
      const lng = data?.result?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setStatus({ state: 'error', message: 'Could not read coordinates.' });
        return;
      }
      setHome({ lat, lng, label: raw.toUpperCase() });
      setStatus({ state: 'idle', message: '' });
    } catch {
      setStatus({ state: 'error', message: 'Network issue. Try again.' });
    }
  };

  const filtered = useMemo(() => {
    return PLACES.filter((p) => kindFilter === 'all' || p.kind.toLowerCase() === kindFilter);
  }, [kindFilter]);

  const ranked = useMemo(() => {
    if (!home) return [];
    return filtered.map((p) => ({ ...p, km: haversineKm(home, p) })).sort((a, b) => a.km - b.km).slice(0, 6);
  }, [filtered, home]);

  return (
    <>
      <div className="page-head">
        <Link to="/apply?tab=we" className="back-link"><ArrowLeft width="16" height="16" />Back to Work experience</Link>
        <h1 style={{ marginTop: 12 }}>Work experience near you</h1>
        <p className="subtitle">Map of major NHS hospitals and hospices. Enter your postcode for the closest six.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={postcode} onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
            placeholder="Your postcode (e.g. SW1A 1AA)"
            style={{ flex: '1 1 200px', padding: '11px 14px', fontSize: 15, fontWeight: 600, border: '1px solid var(--border-strong)', borderRadius: 10, textTransform: 'uppercase' }}
          />
          <button onClick={lookup} disabled={status.state === 'loading'} className="btn btn-primary">
            {status.state === 'loading' ? 'Looking up…' : 'Find nearest'} <ArrowRight width="14" height="14" />
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['all', 'All'], ['hospital', 'Hospitals'], ['hospice', 'Hospices']].map(([k, label]) => (
              <button key={k} onClick={() => setKindFilter(k)} style={pill(kindFilter === k)}>{label}</button>
            ))}
          </div>
        </div>
        {status.state === 'error' && <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{status.message}</p>}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 18, height: 460, position: 'relative' }}>
        <MapContainer center={UK_CENTRE} zoom={6} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          {home && <FlyTo coords={home} />}
          {home && (
            <>
              <Marker position={[home.lat, home.lng]} icon={userIcon}>
                <Popup>You — {home.label}</Popup>
              </Marker>
              <CircleMarker center={[home.lat, home.lng]} radius={40} pathOptions={{ color: '#23143f', fillOpacity: 0.05, weight: 1.5 }} />
            </>
          )}
          {filtered.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={kindIcon[p.kind]}>
              <Popup>
                <strong>{p.name}</strong><br />
                {p.kind} · {p.city}
              </Popup>
            </Marker>
          ))}
          {CAREER_CAMP.map((c) => (
            <Marker key={c.id} position={[c.lat, c.lng]} icon={kindIcon['Career Camp']} zIndexOffset={1000}>
              <Popup>
                <strong>{c.name}</strong><br />
                <span style={{ color: '#653192', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Medic Mind · Bookable</span>
                <p style={{ margin: '6px 0 8px', fontSize: 13 }}>{c.info}</p>
                <a href="/career-camp" style={{ display: 'inline-block', padding: '6px 12px', background: 'linear-gradient(135deg,#653192,#3ab8a6)', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>See cohort →</a>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        <div style={floatingCta}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <Stethoscope width="20" height="20" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.85 }}>Featured</div>
              <strong style={{ fontSize: 14, display: 'block', lineHeight: 1.2 }}>Medic Mind Career Camp</strong>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#e9d5ff' }}>Real NHS hospital + hospice. Bursaries up to 100%.</p>
            </div>
          </div>
          <Link to="/career-camp" style={floatingBtn}>See cohorts <ArrowRight width="14" height="14" /></Link>
        </div>
      </div>

      <style>{`@keyframes mm-pulse { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.4); opacity: 0; } }`}</style>

      {ranked.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Closest to {home?.label}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ranked.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--card-soft)', borderRadius: 10 }}>
                <div>
                  <strong style={{ fontSize: 14 }}>{p.name}</strong>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.kind} · {p.city}</div>
                </div>
                <div style={{ color: 'var(--brand-purple)', fontWeight: 700, fontSize: 14 }}>{p.km} km</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Pin coordinates are approximate city-site locations. To apply for work experience, contact each NHS trust directly — most have a work-experience coordinator. Career Camp also runs bookable cohorts in <Link to="/career-camp" style={{ color: 'var(--accent)', fontWeight: 600 }}>London, Birmingham and Manchester</Link>.
      </p>
    </>
  );
}

const floatingCta = {
  position: 'absolute', bottom: 16, left: 16, right: 16,
  maxWidth: 460, margin: '0 auto',
  background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
  color: '#fff', borderRadius: 14, padding: '14px 16px',
  display: 'flex', flexDirection: 'column', gap: 10,
  boxShadow: '0 12px 32px rgba(35, 20, 63, 0.35)',
  zIndex: 500, pointerEvents: 'auto'
};
const floatingBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', color: 'var(--brand-deep)',
  borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none'
};

function pill(active) {
  return {
    padding: '8px 12px', borderRadius: 99, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: active ? 'var(--accent-soft)' : '#fff',
    color: active ? 'var(--brand-deep)' : 'var(--text-2)',
    fontWeight: 600
  };
}
